import { useQuery } from "@apollo/client";
import { ScreenHeader } from "common/ScreenHeader";
import _ from "lodash";
import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  AppRoleWithAssignees,
  FetchAppRolesQuery,
  FetchGroupsQuery,
  FetchUsersQuery,
  FetchStoragePermissionsQuery,
  PermissionGroup,
  PermissionUserAppRole,
  StoragePermissionsResult
} from ".";
import { makeStyles } from "makeStyles";
import { Skeleton, Tab, Tabs } from "@mui/material";
import AppAccessPermissions from "./permissions/PermissionsTab";
import { TabContext, TabPanel } from "@mui/lab";
import { permissionsTableStyles } from "./styles";
import AppAccessGroups from "./groups/GroupsTab";
import { User } from "users";
import AppAccessUsers from "./users/UsersTab";
import { InaType } from "workflow-inas/models";
import { InaTypesQuery } from "workflow-inas/queries";
import AppAccessFiles from "./files/FilesTab";
import AppAccessScreens from "./screens/ScreensTab";

const useStyles = makeStyles()((theme) => ({
  ...permissionsTableStyles(theme),
  tabsList: {
    //borderBottom: `1px solid ${theme.palette.divider}`
  },
  tabPanel: {
    border: `1px solid ${theme.palette.divider}`
  }
}));

export enum AppAccessTabs {
  Permissions = "permissions",
  Groups = "groups",
  Users = "users",
  Files = "files",
  Screens = "screens"
}

const AppRolesScreen: React.FunctionComponent = () => {
  const { classes } = useStyles();
  const [activeTab, setActiveTab] = useState(AppAccessTabs.Permissions);

  const appRolesQuery = useQuery<{ appRoles: AppRoleWithAssignees[] }, {}>(FetchAppRolesQuery, {
    variables: {}
  });
  const appRoles = React.useMemo(
    () => (appRolesQuery.data?.appRoles ? _.orderBy(appRolesQuery.data.appRoles, (ar) => ar.identifier) : []),
    [appRolesQuery.data?.appRoles]
  );

  const groupsQuery = useQuery<{ groups: PermissionGroup[] }, {}>(FetchGroupsQuery, {
    variables: {}
  });
  const groupsUncombined = React.useMemo(
    () => (groupsQuery.data?.groups ? _.orderBy(groupsQuery.data.groups, (g) => g.name) : []),
    [groupsQuery.data?.groups]
  );

  const inaTypesQuery = useQuery<{ inaTypes: InaType[] }>(InaTypesQuery);
  const inaTypes = React.useMemo(
    () => (inaTypesQuery.data?.inaTypes ? _.orderBy(inaTypesQuery.data.inaTypes, (i) => i.id) : []),
    [inaTypesQuery.data?.inaTypes]
  );

  const groupsCombined = React.useMemo(() => {
    return groupsUncombined.map((group) => {
      return {
        ...group,
        permissions: appRoles.filter((role) => role.assignees.some((assignee) => assignee.azureObjectId === group.azureObjectId)),
        inas: inaTypes
          .filter(
            (ina) =>
              ina.assignedToRoleNormally?.id === group.prsGroupId ||
              ina.assignedToRoleForNonAssuranceReviews?.id === group.prsGroupId ||
              ina.assignedToRoleForPprpProgramReviews?.id === group.prsGroupId
          )
          .map((ina) => ({
            id: ina.id,
            inaName: ina.friendlyName,
            forDefault: ina.assignedToRoleNormally?.id === group.prsGroupId,
            forNonAssurance: ina.assignedToRoleForNonAssuranceReviews?.id === group.prsGroupId,
            forPprp: ina.assignedToRoleForPprpProgramReviews?.id === group.prsGroupId
          }))
      };
    });
  }, [appRoles, groupsUncombined, inaTypes]);

  const usersQuery = useQuery<{ users: User[] }, {}>(FetchUsersQuery, {
    variables: {}
  });
  const usersUncombined = React.useMemo(
    () => (usersQuery.data?.users ? _.orderBy(usersQuery.data.users, (u) => u.name) : []),
    [usersQuery.data?.users]
  );

  const usersCombined = React.useMemo(() => {
    return usersUncombined.map((user) => {
      const groups = groupsCombined.filter((g) => g.members?.some((m) => m.id === user.id));

      // Collect all INAs from all groups the user is in
      const inaMap: Record<
        string,
        {
          id: number;
          inaName: string;
          viaDefault?: string;
          viaNonAssurance?: string;
          viaPprp?: string;
        }
      > = {};
      const permissionsMap: Record<string, PermissionUserAppRole> = {};

      groups.forEach((group) => {
        group.inas.forEach((ina) => {
          if (!inaMap[ina.id]) {
            inaMap[ina.id] = {
              id: ina.id,
              inaName: ina.inaName
            };
          }
          if (ina.forDefault) {
            inaMap[ina.id].viaDefault = group.name;
          }
          if (ina.forNonAssurance) {
            inaMap[ina.id].viaNonAssurance = group.name;
          }
          if (ina.forPprp) {
            inaMap[ina.id].viaPprp = group.name;
          }
        });

        group.permissions.forEach((role) => {
          if (!permissionsMap[role.id]) {
            permissionsMap[role.id] = {
              identifier: role.identifier,
              displayName: role.displayName,
              description: role.description,
              id: role.id,
              via: []
            };
          }
          permissionsMap[role.id].via?.push(group.name);
        });
      });

      return {
        ...user,
        inas: Object.values(inaMap),
        appRoles: Object.values(permissionsMap)
      };
    });
  }, [usersUncombined, groupsCombined]);

  const storagePermissionsQuery = useQuery<{ storagePermissions: StoragePermissionsResult[] }>(FetchStoragePermissionsQuery);

  return (
    <>
      <Helmet>
        <title>App Access Details - PRS Online</title>
      </Helmet>

      <ScreenHeader title="App Access Details" />
      <Tabs value={activeTab} className={classes.tabsList} onChange={(_, newValue: AppAccessTabs) => setActiveTab(newValue)}>
        <Tab label="Permissions" value={AppAccessTabs.Permissions} />
        <Tab label="Groups" value={AppAccessTabs.Groups} />
        <Tab label="Users" value={AppAccessTabs.Users} />
        <Tab label="Screens" value={AppAccessTabs.Screens} />
        <Tab label="SharePoint" value={AppAccessTabs.Files} />
      </Tabs>
      <TabContext value={activeTab}>
        <TabPanel value={AppAccessTabs.Permissions} className={classes.tabPanel}>
          {appRolesQuery.loading ? (
            <Skeleton variant="rectangular" width="100%" height={400} />
          ) : (
            <AppAccessPermissions permissions={appRoles} />
          )}
        </TabPanel>
        <TabPanel value={AppAccessTabs.Groups} className={classes.tabPanel}>
          {appRolesQuery.loading || groupsQuery.loading || inaTypesQuery.loading ? (
            <Skeleton variant="rectangular" width="100%" height={400} />
          ) : (
            <AppAccessGroups groups={groupsCombined} />
          )}
        </TabPanel>
        <TabPanel value={AppAccessTabs.Users} className={classes.tabPanel}>
          {appRolesQuery.loading || groupsQuery.loading || inaTypesQuery.loading || usersQuery.loading ? (
            <Skeleton variant="rectangular" width="100%" height={400} />
          ) : (
            <AppAccessUsers users={usersCombined} />
          )}
        </TabPanel>
        <TabPanel value={AppAccessTabs.Screens} className={classes.tabPanel}>
          {appRolesQuery.loading ? (
            <Skeleton variant="rectangular" width="100%" height={400} />
          ) : (
            <div>
              <AppAccessScreens permissions={appRoles} />
            </div>
          )}
        </TabPanel>
        <TabPanel value={AppAccessTabs.Files} className={classes.tabPanel}>
          {storagePermissionsQuery.loading ? (
            <Skeleton variant="rectangular" width="100%" height={400} />
          ) : (
            <div>
              <AppAccessFiles storages={storagePermissionsQuery.data?.storagePermissions} />
            </div>
          )}
        </TabPanel>
      </TabContext>
    </>
  );
};

export default AppRolesScreen;
